/* eslint-disable write-good-comments/write-good-comments -- rule names contain "only" which triggers false positives in oxlint directives */
// hooks/use-tutorial-progress.ts
// Follows react-patterns skill: Controller hook pattern for testability
"use client";

import { useCallback, useEffect, useMemo, useState } from "react";

import { consoleOutput, createLogger } from "@vllnt/logger";

const logger = createLogger("tutorial-progress", {
  getLogLevel: () => "info",
  output: consoleOutput,
});

import {
  isValidProgressStore,
  STORAGE_KEY,
  type TutorialProgressEntry,
  type TutorialProgressStore,
} from "@/types/tutorial-progress";

// Types - exported separately for testing
export type UseTutorialProgressOptions = {
  contentId: string;
  sectionIds: string[];
  tutorialVersion: number; // Current tutorial content version from MDX
};

export type UseTutorialProgressController = {
  // State
  completedSections: Set<string>;
  // Computed
  completionCount: number;
  completionPercent: number;
  currentSection?: string;
  hasProgress: boolean;

  isComplete: boolean;
  isLoading: boolean;
  markSectionComplete: (sectionId: string) => void;
  resetProgress: () => void;

  setCurrentSection: (sectionId: string) => void;
  storageAvailable: boolean; // ADR-006: localStorage availability flag
  // Actions
  toggleSection: (sectionId: string) => void;
  totalSections: number;
};

type LoadResult = { available: boolean; store?: TutorialProgressStore };

// Check localStorage availability
function isLocalStorageAvailable(): boolean {
  try {
    const testKey = "__storage_test__";
    localStorage.setItem(testKey, testKey);
    localStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
}

// Parse stored data with schema check
function parseStoredProgress(raw: null | string): {
  store?: TutorialProgressStore;
} {
  if (!raw) return {};

  const parsed: unknown = JSON.parse(raw);
  if (!isValidProgressStore(parsed)) return {};

  return { store: parsed };
}

// Helper: Load store from localStorage with graceful degradation
function loadStoreFromLocalStorage(): LoadResult {
  if (!isLocalStorageAvailable()) {
    logger.warn("local-storage-unavailable", {});
    return { available: false };
  }

  try {
    const result = parseStoredProgress(localStorage.getItem(STORAGE_KEY));
    return { available: true, ...result };
  } catch (error) {
    logger.warn("invalid-progress-data-resetting", {
      error: error instanceof Error ? error.message : String(error),
    });
    return { available: true };
  }
}

// Controller hook (testable, reusable)
// eslint-disable-next-line max-lines-per-function -- Complex hook coordinating state, localStorage, and computed values per TDD spec
export function useTutorialProgressController({
  contentId,
  sectionIds,
  tutorialVersion,
}: UseTutorialProgressOptions): UseTutorialProgressController {
  const [store, setStore] = useState<TutorialProgressStore | undefined>();
  const [isLoading, setIsLoading] = useState(true);
  const [storageAvailable, setStorageAvailable] = useState(true);

  // Save to localStorage
  const saveStore = useCallback((newStore: TutorialProgressStore) => {
    setStore(newStore);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newStore));
    } catch (error) {
      logger.warn("save-progress-failed", {
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }, []);

  // Load from localStorage on mount (ADR-006: graceful degradation)
  // Also check per-tutorial version and reset if outdated
  // oxlint-disable-next-line react-doctor/no-cascading-set-state -- mount-only effect; setStorageAvailable + setStore + setIsLoading are coordinated initialization, not cascading state.
  useEffect(() => {
    const { available, store: loadedStore } = loadStoreFromLocalStorage();
    setStorageAvailable(available);

    if (loadedStore) {
      const entry = loadedStore.tutorials[contentId];

      // Per-tutorial version check: reset this tutorial if version mismatch
      if (entry && entry.version !== tutorialVersion) {
        logger.info("version-mismatch-resetting", {
          contentId,
          newVersion: tutorialVersion,
          oldVersion: entry.version,
        });
        const { [contentId]: _, ...remainingTutorials } = loadedStore.tutorials;
        const updatedStore = { tutorials: remainingTutorials };
        saveStore(updatedStore);
        setStore(updatedStore);
      } else {
        setStore(loadedStore);
      }
    }

    setIsLoading(false);
  }, [contentId, tutorialVersion, saveStore]);

  // Get current tutorial entry
  const entry = store?.tutorials[contentId];

  // Create a Set of valid section IDs for fast lookup
  const validSectionIds = useMemo(() => new Set(sectionIds), [sectionIds]);

  // Computed values - memoized to avoid dependencies issues
  // Reconciliation: Filter to sections that exist in current content
  const completedSections = useMemo(
    () =>
      new Set<string>(
        Object.entries(entry?.sections ?? {}).flatMap(([id, s]) =>
          s.completed && validSectionIds.has(id) ? [id] : [],
        ),
      ),
    [entry?.sections, validSectionIds],
  );

  // Reconciliation: Clean up stale section IDs from localStorage
  useEffect(() => {
    if (isLoading || !store || !entry?.sections) return;

    const storedSectionIds = Object.keys(entry.sections);
    const staleSectionIds = storedSectionIds.filter(
      (id) => !validSectionIds.has(id),
    );

    // If there are stale sections, clean them up
    if (staleSectionIds.length > 0) {
      const cleanedSections = Object.fromEntries(
        Object.entries(entry.sections).filter(([id]) =>
          validSectionIds.has(id),
        ),
      );

      const cleanedEntry: TutorialProgressEntry = {
        ...entry,
        sections: cleanedSections,
      };

      saveStore({
        tutorials: { ...store.tutorials, [contentId]: cleanedEntry },
      });
    }
  }, [isLoading, store, entry, contentId, validSectionIds, saveStore]);

  const completionCount = completedSections.size;
  const totalSections = sectionIds.length;
  const completionPercent =
    totalSections > 0 ? Math.round((completionCount / totalSections) * 100) : 0;
  const isComplete = completionCount === totalSections && totalSections > 0;

  // Toggle section completion
  const toggleSection = useCallback(
    (sectionId: string) => {
      const now = new Date().toISOString();
      const currentEntry = store?.tutorials[contentId];
      const isCurrentlyCompleted =
        currentEntry?.sections[sectionId]?.completed ?? false;

      const newEntry: TutorialProgressEntry = {
        lastSection: sectionId,
        lastVisited: now,
        sections: {
          ...currentEntry?.sections,
          [sectionId]: {
            completed: !isCurrentlyCompleted,
            completedAt: isCurrentlyCompleted ? undefined : now,
          },
        },
        startedAt: currentEntry?.startedAt ?? now,
        version: tutorialVersion,
      };

      saveStore({
        tutorials: { ...store?.tutorials, [contentId]: newEntry },
      });
    },
    [contentId, store, saveStore, tutorialVersion],
  );

  // Mark section complete (no toggle)
  const markSectionComplete = useCallback(
    (sectionId: string) => {
      if (completedSections.has(sectionId)) return;
      toggleSection(sectionId);
    },
    [completedSections, toggleSection],
  );

  // Set current section (for navigation tracking)
  const setCurrentSection = useCallback(
    (sectionId: string) => {
      const now = new Date().toISOString();
      const currentEntry = store?.tutorials[contentId];

      const newEntry: TutorialProgressEntry = {
        lastSection: sectionId,
        lastVisited: now,
        sections: currentEntry?.sections ?? {},
        startedAt: currentEntry?.startedAt ?? now,
        version: tutorialVersion,
      };

      saveStore({
        tutorials: { ...store?.tutorials, [contentId]: newEntry },
      });
    },
    [contentId, store, saveStore, tutorialVersion],
  );

  // Reset progress
  const resetProgress = useCallback(() => {
    if (!store) return;
    const { [contentId]: _, ...remainingTutorials } = store.tutorials;
    saveStore({ tutorials: remainingTutorials });
  }, [contentId, store, saveStore]);

  return {
    // State
    completedSections,
    // Computed
    completionCount,
    completionPercent,
    currentSection: entry?.lastSection,
    hasProgress: !!entry,
    isComplete,
    isLoading,
    markSectionComplete,
    resetProgress,
    setCurrentSection,
    storageAvailable,
    // Actions
    toggleSection,
    totalSections,
  };
}

// Re-export as useTutorialProgress for backwards compatibility
export const useTutorialProgress = useTutorialProgressController;
